// ─────────────────────────────────────────────────────────────────────────────
// selection_widget.ts — Unified selection widget
//
// The atomic interactive selection unit. A widget holds a flat list of
// selectable items inside a bounding box. Strokes that touch (or pass close
// to) any item select that item — no circle gesture required.
//
// Selection rules:
//   TAP     (very short stroke / dot)  → centre → nearest item
//   DRAW-OVER / NEAR (everything else) → score each item by how many
//                                        stroke points fall within its hit
//                                        radius; pick the winner
//   NO-HIT (no item scored)             → passthrough (caller fades the ink)
//
// Selection feedback is per-item-kind: a button press, a checkbox tick, a
// swatch outline pulse, or an icon glow. The dialog renders the resting
// "selected" state itself (bold label, checked box, …); the widget plays
// the transient animation and then gets out of the way.
// ─────────────────────────────────────────────────────────────────────────────

// ── Public types ─────────────────────────────────────────────────────────────

export type ItemKind = 'button' | 'checkbox' | 'icon' | 'swatch' | 'toggle';

export interface WidgetItem {
  cx: number;       // centre x (page-local)
  cy: number;       // centre y (page-local)
  r: number;        // hit / visual radius
  group: number;
  value: unknown;
  /** Drives the per-item selection animation. Defaults to 'button'. */
  kind?: ItemKind;
  /** Optional per-item custom visual state (overrides default rendering). */
  visualState?: WidgetVisualState;
}

/**
 * Classification of how the user interacted with the widget.
 * Retained for callers that branch on it; new logic shouldn't depend on the
 * specific kind beyond differentiating "tap" (very short) from everything
 * else, since circle/cross gating no longer applies.
 */
export type GestureKind = 'tap' | 'over';

export interface WidgetSelectionResult {
  item: WidgetItem;
  gesture: GestureKind;
}

/**
 * Custom visual state interface. Adventure mode and protection layers
 * implement this to render their own resting visuals when an item is
 * pre-selected. The dialog/widget calls drawActive each frame the item
 * is selected; drawInactive is optional.
 */
export interface WidgetVisualState {
  drawActive(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, now: number): void;
  drawInactive?(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, now: number): void;
}

// ── Animation master gate ───────────────────────────────────────────────────

let _animationsEnabled = true;

/** Master switch for selection animations (per-kind effects: button press,
 *  checkbox tick, swatch pulse, icon glow). When off, `applySelection` still
 *  records the selected mark and fires `onSelect`, but no animation queues.
 *  Used by the Settings → Visuals "Animations" toggle. */
export function setSelectionAnimationsEnabled(v: boolean): void {
  _animationsEnabled = v;
}

// ── Animation timings ───────────────────────────────────────────────────────

const FX_BUTTON_MS   = 240;
const FX_CHECKBOX_MS = 280;
const FX_SWATCH_MS   = 320;
const FX_ICON_MS     = 220;

const FX_MAX_MS = Math.max(FX_BUTTON_MS, FX_CHECKBOX_MS, FX_SWATCH_MS, FX_ICON_MS);

const FX_COLOR = '#3a2010';
const FX_GLOW  = '#ffe8a0';

/** Minimum arc length (px) below which a stroke is treated as a tap. */
const TAP_THRESHOLD = 40;

/** How far past `item.r` a stroke point can be while still counting as
 *  "over" the item. Lets the user be sloppy without missing nearby items. */
const PROXIMITY_PADDING = 14;

// ── Internal records ────────────────────────────────────────────────────────

interface SelectEffect {
  kind: ItemKind;
  cx: number;
  cy: number;
  r: number;
  group: number;
  startedAt: number;
}

interface SelectedMark {
  item: WidgetItem;
  group: number;
}

// ── Stroke geometry helpers (still exported; kept for back-compat) ───────────

function strokeArcLength(pts: Array<{ x: number; y: number }>): number {
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    len += Math.sqrt(dx * dx + dy * dy);
  }
  return len;
}

function strokeCentroid(pts: Array<{ x: number; y: number }>): { x: number; y: number } {
  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  return { x: sx / pts.length, y: sy / pts.length };
}

/**
 * Legacy: detect closed-loop strokes. The widget no longer requires this
 * for selection — kept only because `selection_region.ts` re-exports it
 * for callers that haven't migrated.
 * @deprecated Use `analyzeStroke` instead.
 */
export function isEncirclement(pts: Array<{ x: number; y: number }>): boolean {
  if (pts.length < 8) return false;
  const s = pts[0], e = pts[pts.length - 1];
  const closeDist = Math.sqrt((s.x - e.x) ** 2 + (s.y - e.y) ** 2);
  if (closeDist > 50) return false;
  let area = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    area += pts[i].x * pts[i + 1].y - pts[i + 1].x * pts[i].y;
  }
  return Math.abs(area) > 800;
}

/**
 * Legacy: detect mostly-straight strokes. No longer used as a selection gate.
 * @deprecated Use `analyzeStroke` instead.
 */
export function isCrossLine(pts: Array<{ x: number; y: number }>): boolean {
  if (pts.length < 3) return false;
  const s = pts[0], e = pts[pts.length - 1];
  const closeDist = Math.sqrt((s.x - e.x) ** 2 + (s.y - e.y) ** 2);
  let pathLen = 0;
  for (let i = 1; i < pts.length; i++) {
    pathLen += Math.sqrt((pts[i].x - pts[i - 1].x) ** 2 + (pts[i].y - pts[i - 1].y) ** 2);
  }
  if (pathLen < 30) return false;
  const directness = closeDist / pathLen;
  return directness > 0.4 && closeDist > 20;
}

// ── Easing ──────────────────────────────────────────────────────────────────

function easeOutCubic(t: number): number { return 1 - (1 - t) ** 3; }
function easeOutBack(t: number): number {
  const c1 = 1.70158, c3 = c1 + 1;
  return 1 + c3 * (t - 1) ** 3 + c1 * (t - 1) ** 2;
}

// ── SelectionWidget ─────────────────────────────────────────────────────────

export class SelectionWidget {
  items: WidgetItem[] = [];

  /** Bounding box of the entire widget (page-local). */
  bbox = { x: 0, y: 0, w: 0, h: 0 };

  /** Margin around items for hit-testing. */
  margin = 20;

  /** Default custom visual state for all items (overridden per item). */
  defaultVisualState: WidgetVisualState | null = null;

  /** Callback fired when a selection changes. */
  onSelect?: (group: number, item: WidgetItem, gesture: GestureKind) => void;

  // ── Visual state ──────────────────────────────────────────────────────

  /** One mark per group: which item is currently selected. Used by callers
   *  with custom visual states; rendering is opt-in via `drawActive`. */
  private _marks = new Map<number, SelectedMark>();
  private _effects: SelectEffect[] = [];

  // ── Items / bbox ──────────────────────────────────────────────────────

  updateItems(items: WidgetItem[]): void {
    this.items = items;
    if (items.length === 0) {
      this.bbox = { x: 0, y: 0, w: 0, h: 0 };
      return;
    }
    const M = this.margin;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const it of items) {
      x0 = Math.min(x0, it.cx - it.r - M);
      y0 = Math.min(y0, it.cy - it.r - M);
      x1 = Math.max(x1, it.cx + it.r + M);
      y1 = Math.max(y1, it.cy + it.r + M);
    }
    this.bbox = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  preSelect(group: number, itemIndex: number): void {
    const it = this.items[itemIndex];
    if (!it) return;
    this._marks.set(group, { item: it, group });
  }

  hasRing(group: number): boolean { return this._marks.has(group); }

  clear(): void {
    this._marks.clear();
    this._effects = [];
  }

  // ── Stroke analysis ───────────────────────────────────────────────────

  /** Quick test: does the stroke cross this widget's bbox? */
  strokeCrossesRegion(pts: Array<{ x: number; y: number }>): boolean {
    const b = this.bbox;
    const m = 8;
    for (const p of pts) {
      if (p.x >= b.x - m && p.x <= b.x + b.w + m &&
          p.y >= b.y - m && p.y <= b.y + b.h + m) {
        return true;
      }
    }
    return false;
  }

  /**
   * Selection rules — no circle gesture required:
   *   1. Stroke arc length < TAP_THRESHOLD → tap → nearest item by centroid.
   *   2. Otherwise → score each item by how many stroke points fall within
   *      `item.r + PROXIMITY_PADDING`. Highest score wins. Ties resolved
   *      by closest centroid.
   *   3. No item scored > 0 → null (caller should fade the stroke).
   */
  analyzeStroke(
    pts: Array<{ x: number; y: number }>,
  ): WidgetSelectionResult | null {
    if (pts.length === 0) return null;
    if (pts.length === 1) {
      return this._findNearest(pts[0], 'tap');
    }

    const arcLen = strokeArcLength(pts);
    if (arcLen < TAP_THRESHOLD) {
      return this._findNearest(strokeCentroid(pts), 'tap');
    }

    return this._findMostCovered(pts);
  }

  private _findNearest(
    centre: { x: number; y: number },
    gesture: GestureKind,
  ): WidgetSelectionResult | null {
    let best: WidgetItem | null = null;
    let bestDist = Infinity;
    for (const it of this.items) {
      const dx = centre.x - it.cx;
      const dy = centre.y - it.cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < bestDist) { bestDist = d; best = it; }
    }
    return best ? { item: best, gesture } : null;
  }

  /** Pick the item with the most stroke points within reach. Ties broken
   *  by smallest centroid distance. Returns null when no item is touched. */
  private _findMostCovered(
    pts: Array<{ x: number; y: number }>,
  ): WidgetSelectionResult | null {
    if (this.items.length === 0) return null;
    let bestItem: WidgetItem | null = null;
    let bestScore = 0;
    let bestDist = Infinity;
    const cx = strokeCentroid(pts);

    for (const it of this.items) {
      const reach = it.r + PROXIMITY_PADDING;
      const reachSq = reach * reach;
      let score = 0;
      for (const p of pts) {
        const dx = p.x - it.cx;
        const dy = p.y - it.cy;
        if (dx * dx + dy * dy <= reachSq) score++;
      }
      if (score === 0) continue;
      const dx = cx.x - it.cx;
      const dy = cx.y - it.cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (score > bestScore || (score === bestScore && d < bestDist)) {
        bestItem = it;
        bestScore = score;
        bestDist = d;
      }
    }

    return bestItem ? { item: bestItem, gesture: 'over' } : null;
  }

  // ── Selection application ─────────────────────────────────────────────

  applySelection(
    item: WidgetItem,
    gesture: GestureKind,
    _strokePts?: Array<{ x: number; y: number }>,
  ): void {
    void gesture;  // gesture kept for callback compatibility; not used here
    // Replace the mark for this group.
    this._marks.set(item.group, { item, group: item.group });
    // Drop any in-flight effect for this group so the new one starts clean.
    this._effects = this._effects.filter(e => e.group !== item.group);
    if (_animationsEnabled) {
      this._effects.push({
        kind: item.kind ?? 'button',
        cx: item.cx,
        cy: item.cy,
        r: item.r,
        group: item.group,
        startedAt: performance.now(),
      });
    }
    this.onSelect?.(item.group, item, gesture);
  }

  // ── Rendering ─────────────────────────────────────────────────────────

  draw(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
    const now = performance.now();

    // Custom-visualState marks (e.g. adventure / protection layers). These
    // render the resting "selected" indicator. Plain dialogs skip this —
    // their selected styling lives in the option layer above us.
    for (const mark of this._marks.values()) {
      const vs = mark.item.visualState ?? this.defaultVisualState;
      if (vs) {
        vs.drawActive(ctx, ox + mark.item.cx, oy + mark.item.cy, mark.item.r, now);
      }
    }

    // Per-kind transient animations. Each effect runs once and disappears.
    this._effects = this._effects.filter(eff => {
      const elapsed = now - eff.startedAt;
      const lifeMs = effectLifetimeMs(eff.kind);
      if (elapsed >= lifeMs) return false;
      drawEffect(ctx, ox, oy, eff, elapsed / lifeMs);
      return true;
    });
  }

  get animating(): boolean {
    return this._effects.length > 0;
  }
}

// ── Per-kind effect renderers ───────────────────────────────────────────────

function effectLifetimeMs(kind: ItemKind): number {
  switch (kind) {
    case 'button':   return FX_BUTTON_MS;
    case 'checkbox':
    case 'toggle':   return FX_CHECKBOX_MS;
    case 'swatch':   return FX_SWATCH_MS;
    case 'icon':     return FX_ICON_MS;
  }
}

function drawEffect(
  ctx: CanvasRenderingContext2D,
  ox: number, oy: number,
  eff: SelectEffect,
  t: number,
): void {
  const cx = ox + eff.cx;
  const cy = oy + eff.cy;
  switch (eff.kind) {
    case 'button':         drawButtonPress(ctx, cx, cy, eff.r, t); break;
    case 'checkbox':
    case 'toggle':         drawCheckboxTick(ctx, cx, cy, eff.r, t); break;
    case 'swatch':         drawSwatchPulse(ctx, cx, cy, eff.r, t); break;
    case 'icon':           drawIconGlow(ctx, cx, cy, eff.r, t); break;
  }
}

/** Button press: brief inward squeeze + alpha shimmer ring. */
function drawButtonPress(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, t: number,
): void {
  const ringR = r * (1 + 0.35 * easeOutCubic(t));
  const alpha = (1 - t) * 0.55;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = FX_COLOR;
  ctx.lineWidth = 1.2 * (1 - t * 0.6);
  ctx.beginPath();
  ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Checkbox tick: small check stroke that draws on then fades. */
function drawCheckboxTick(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, t: number,
): void {
  const traceT = Math.min(1, t / 0.7);
  const fadeT = t > 0.7 ? (t - 0.7) / 0.3 : 0;
  const alpha = (1 - fadeT) * 0.85;
  // Tick path (anchored to item centre, scaled to r * 0.85)
  const s = r * 0.85;
  const p0 = { x: cx - s * 0.5, y: cy };
  const p1 = { x: cx - s * 0.1, y: cy + s * 0.45 };
  const p2 = { x: cx + s * 0.55, y: cy - s * 0.45 };
  // Two-segment animation: 0..0.5 = p0→p1, 0.5..1 = p1→p2
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = FX_COLOR;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(p0.x, p0.y);
  if (traceT <= 0.5) {
    const u = traceT / 0.5;
    ctx.lineTo(p0.x + (p1.x - p0.x) * u, p0.y + (p1.y - p0.y) * u);
  } else {
    ctx.lineTo(p1.x, p1.y);
    const u = (traceT - 0.5) / 0.5;
    ctx.lineTo(p1.x + (p2.x - p1.x) * u, p1.y + (p2.y - p1.y) * u);
  }
  ctx.stroke();
  ctx.restore();
}

/** Swatch outline pulse: ring expands outward and fades. */
function drawSwatchPulse(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, t: number,
): void {
  const eased = easeOutCubic(t);
  const ringR = r * (1.05 + 0.55 * eased);
  const alpha = (1 - t) * 0.7;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = FX_COLOR;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
  ctx.stroke();
  // Thin highlight halo flashing in early
  if (t < 0.5) {
    const haloA = (1 - t / 0.5) * 0.5;
    ctx.globalAlpha = haloA;
    ctx.shadowColor = FX_GLOW;
    ctx.shadowBlur = 6;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.05, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** Icon glow: brief radial highlight that pops then fades. */
function drawIconGlow(
  ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, t: number,
): void {
  const peakAt = 0.35;
  const intensity = t < peakAt
    ? easeOutBack(t / peakAt)
    : 1 - easeOutCubic((t - peakAt) / (1 - peakAt));
  ctx.save();
  ctx.globalAlpha = Math.max(0, intensity) * 0.7;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.6);
  grad.addColorStop(0, 'rgba(255,232,160,0.85)');
  grad.addColorStop(1, 'rgba(255,232,160,0)');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// ── Animation lifetime export (for canvas redraw scheduling) ────────────────

export const SELECTION_FX_MAX_MS = FX_MAX_MS;
